import React, { useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import {
  faCircleExclamation,
  faSpinnerThird,
} from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Honeybadger from '@honeybadger-io/js';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert } from '../../../../../shared/components/Alert';

const DeleteButton = ({ children, isLoading, onClick }) => {
  if (isLoading) {
    return (
      <AlertDialogAction disabled>
        <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
        Please wait
      </AlertDialogAction>
    );
  }

  return <AlertDialogAction onClick={onClick}>{children}</AlertDialogAction>;
};

DeleteButton.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const DeleteDialog = ({
  errorTitle,
  isOpen,
  onClose,
  onDelete,
  successMessage,
  description = '',
  item = null,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = () => {
    onClose();
    toast.success(successMessage);
  };

  const handleDelete = async (e) => {
    // <AlertDialogAction /> closes the dialog on click, without waiting for
    // handleDelete() to finish execution. e.preventDefault() prevents this behavior.
    e.preventDefault();

    try {
      setErrorMessage('');
      setIsLoading(true);
      await onDelete(item);
      handleSuccess();
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
      setErrorMessage('');
    }
  };

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>

        {!isEmpty(errorMessage) && (
          <Alert
            description={errorMessage}
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            title={errorTitle}
            variant="destructive"
          />
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>

          <DeleteButton isLoading={isLoading} onClick={handleDelete}>
            Delete
          </DeleteButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const itemShape = PropTypes.shape({
  id: PropTypes.string,
});

DeleteDialog.propTypes = {
  errorTitle: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  successMessage: PropTypes.string.isRequired,
  description: PropTypes.string,
  item: itemShape,
};

export default DeleteDialog;
